import type { ReactNode } from "react";
import type { CampaignSchema } from "@/types";

/**
 * The shared campaign detail shell contract. Each campaign-type module (see
 * `@/campaigns/modules/*`) maps its campaign into these slots via its
 * `detailView` builder; `CampaignShell` renders them. The shell knows nothing
 * about concrete campaign types — only this shape.
 */
export interface CampaignView {
  campaign: CampaignSchema;
  /** Desktop label for the primary tab (mobile always shows "Overview"). */
  primaryLabel: string;
  /** Sidebar content below the campaign banner. */
  sidebar: ReactNode;
  /** Primary tab content. */
  primary: ReactNode;
  /** Info tab content. */
  info: ReactNode;
  /** The type's "how to win" card (How to get points, How it works, …). Desktop keeps it in the
   *  sidebar (the module places it there); phones show it FIRST in the Info tab, so the phone
   *  Overview carries only what is happening in the campaign right now. Pass it already dimmed
   *  for a finished campaign. */
  guide?: ReactNode;
  /** Optional banner above the layout. */
  notice?: ReactNode;
  /** Mobile-only notice rendered under the tabs on every tab view
   *  (e.g. the finished card). */
  mobileNotice?: ReactNode;
  /** Mobile-only sticky action pinned to the bottom (e.g. join CTA) — on-site. In-game the shell
   *  renders the join CTA itself, over the sticky cover, from `canJoin`. */
  mobileStickyAction?: ReactNode;
  /** The player can still opt in (opt-in campaign, not joined, not finished). */
  canJoin?: boolean;
  /** Phones held sideways: the Overview as TWO PANES side by side instead of a scrolling stack —
   *  the screen is ~260px tall under the header, so nothing important may sit below the fold.
   *  `narrow` is a fixed ~228px column (the player's own state, or a short list) with the Opt In
   *  pinned at its foot while `canJoin`; `wide` takes the rest. Each pane scrolls inside itself,
   *  never the page. `narrowSide` puts the narrow pane first or last. Types without it keep the
   *  generic two-column flow. `frame` wraps both panes in one element — for state the two must
   *  share (missions: which step the narrow pane has selected for the wide one to show). */
  landscape?: {
    narrow: ReactNode;
    wide: ReactNode;
    narrowSide: "start" | "end";
    frame?: (panes: ReactNode) => ReactNode;
  };
  /** Landscape header: the type's headline figure in the chip beside the name (jackpot total,
   *  winners left), in place of the time left. */
  headerChip?: ReactNode;
  /** Dim/desaturate the layout. */
  dimContent?: boolean;
  /** Campaign has ended — banner desaturates and shows a "Finished" badge. */
  finished?: boolean;
}

/** Grey-out treatment for sections that are no longer actionable on finished
 *  campaigns (prizes, how-to-win) — dimmed but still legible (Figma 5086:1740).
 *  Shared by the per-type detail builders. */
export const FINISHED_DIM = "opacity-50 saturate-50";
